import { untrack } from 'svelte'

import { fetchGlance } from '../api/client'
import type { Glance } from '../api/types'
import { canonical, type Coords } from '../geolocation'
import { whileVisible } from '../visibility'
import { FORECAST_MAX_AGE_MS } from './location.svelte'

/**
 * The Places list's weather, one `/api/glance` per row (Phase 11 plan §6.3).
 *
 * Asked only while the section is open, at most four at once, and kept for
 * the forecast's five minutes, so reopening Places within that is free. A
 * closed section asks for nothing: `watchGlances` is called by the section
 * and stopped when it unmounts, which also aborts whatever is in flight.
 */

export interface GlanceEntry {
  /** null when the last ask failed: the row shows a dash, never a stale number. */
  glance: Glance | null
  /** When the answer was made (the response's Date), or when it failed. */
  at: number
}

/** Four at once: twenty places is five rounds, and NWS sees no burst. */
export const GLANCE_CONCURRENCY = 4
/** How often an open section checks the ages; a request is made only for an old row. */
const CHECK_MS = 60_000

export const glances = $state({
  byKey: {} as Record<string, GlanceEntry>,
})

/** One forecast cell, the way every selection is canonicalised. */
export function glanceKey(p: Coords): string {
  return `${canonical(p.lat)},${canonical(p.lon)}`
}

export function glanceFor(p: Coords | null | undefined): GlanceEntry | null {
  return p ? (glances.byKey[glanceKey(p)] ?? null) : null
}

/**
 * Keys being asked for, and the signal of the pass asking. A pass that was
 * aborted no longer owns its keys, even before its `finally` has run, so a
 * pass started straight after the abort asks for them again.
 */
const inFlight = new Map<string, AbortSignal | undefined>()

function busy(key: string): boolean {
  return inFlight.has(key) && !inFlight.get(key)?.aborted
}

/**
 * Ask for every point whose row is missing, failed or older than five
 * minutes, `GLANCE_CONCURRENCY` at a time. Stops starting new requests and
 * aborts the running ones when `signal` aborts; an aborted request leaves its
 * row as it was rather than marking it failed.
 */
export async function loadGlances(points: readonly Coords[], signal?: AbortSignal): Promise<void> {
  const now = Date.now()
  const queue: Coords[] = []
  const seen = new Set<string>()
  for (const p of points) {
    const key = glanceKey(p)
    if (seen.has(key) || busy(key)) continue
    seen.add(key)
    const entry = glances.byKey[key]
    if (entry?.glance && now - entry.at < FORECAST_MAX_AGE_MS) continue
    queue.push({ lat: canonical(p.lat), lon: canonical(p.lon) })
  }

  const worker = async () => {
    for (let p = queue.shift(); p && !signal?.aborted; p = queue.shift()) {
      const key = glanceKey(p)
      inFlight.set(key, signal)
      try {
        const { glance, fetchedAt } = await fetchGlance(p.lat, p.lon, signal)
        glances.byKey[key] = { glance, at: fetchedAt.getTime() }
      } catch (e) {
        if (signal?.aborted) return
        console.warn(`glance failed for ${key}`, e)
        glances.byKey[key] = { glance: null, at: Date.now() }
      } finally {
        if (inFlight.get(key) === signal) inFlight.delete(key)
      }
    }
  }
  await Promise.all(Array.from({ length: Math.min(GLANCE_CONCURRENCY, queue.length) }, worker))
}

/**
 * Keep the rows current while the section is open: once now, then on the
 * forecast's rule (an interval while visible, and at once on return).
 * Returns the disposer the section's `$effect` returns.
 *
 * `points` is the one thing an effect calling this tracks, so a place saved
 * while the section is open restarts the watch and is asked for at once.
 * The pass itself is untracked: it reads `glances.byKey`, and an effect that
 * tracked that would restart, aborting every row still in flight, each time
 * one row landed.
 */
export function watchGlances(points: () => readonly Coords[]): () => void {
  const controller = new AbortController()
  const list = points()
  const run = () => untrack(() => void loadGlances(list, controller.signal))
  run()
  const stop = whileVisible(run, CHECK_MS)
  return () => {
    stop()
    controller.abort()
  }
}

/** Tests only. */
export function _resetGlances(): void {
  glances.byKey = {}
  inFlight.clear()
}
